export class HeroTransformation {
	constructor(canvas) {
		this.canvas = canvas;
		this.ctx = canvas.getContext("2d");
		this.container = canvas.closest(".intro");

		if (!this.container) {
			console.warn("HeroTransformation: .intro container not found");
			return;
		}

		const style = getComputedStyle(document.documentElement);
		this.strokeColor =
			style.getPropertyValue("--color-accent").trim() || "#FF5C00";
		this.strokeWidth = 1;

		this.shapes = [];
		this.animationDuration = 1200;
		this.startTime = null;
		this.animationId = null;

		this.handleResizeBound = this.handleResize.bind(this);
		this.animateBound = this.animate.bind(this);

		this.init();
	}

	getDimensions() {
		const rect = this.container.getBoundingClientRect();
		return { width: rect.width, height: rect.height };
	}

	init() {
		this.setupCanvas();
		this.createShapes();
		window.addEventListener("resize", this.handleResizeBound);
		this.animationId = requestAnimationFrame(this.animateBound);
	}

	setupCanvas() {
		const { width, height } = this.getDimensions();
		const dpr = window.devicePixelRatio || 1;

		this.canvas.style.width = `${width}px`;
		this.canvas.style.height = `${height}px`;
		this.canvas.width = width * dpr;
		this.canvas.height = height * dpr;

		this.ctx.scale(dpr, dpr);
		this.width = width;
		this.height = height;
	}

	createShapes() {
		const { width, height } = this;

		// Base size for all shapes
		const size = Math.min(width * 0.2, height * 0.4);

		// Wave Y positions - align bottom edges on the same line
		const bottomLine = height - size * 0.1;
		const triYBottom = bottomLine - size * 0.25;
		const sqYBottom = bottomLine - size * 0.425;

		// Gentle wave steps
		const step = size * 0.4;
		const triYMid1 = triYBottom - step * 0.8;
		const triYMid2 = triYBottom - step * 1.8;
		const triYTop = triYBottom - step * 2.5;
		const sqYMid1 = sqYBottom - step * 0.8;
		const sqYMid2 = sqYBottom - step * 1.8;
		const sqYTop = sqYBottom - step * 2.5;

		// X positions - span full width (first shape left edge, last shape right edge)
		const xStart = size / 2; // First shape center at left edge + half size
		const xEnd = width - size / 2; // Last shape center at right edge - half size
		const xSpan = (xEnd - xStart) / 8; // Spacing between 9 shapes

		// Circle Y - at the peak
		const circleY = triYTop - size * 0.15;

		this.shapes = [
			// 4 triangles: sharp → rounded (scale up with radius to compensate for corner cuts)
			{ type: "triangle", x: xStart, y: triYBottom, size: size * 1.0, radius: 0 },
			{ type: "triangle", x: xStart + xSpan, y: triYMid1, size: size * 1.08, radius: size * 0.1 },
			{ type: "triangle", x: xStart + xSpan * 2, y: triYMid2, size: size * 1.15, radius: size * 0.2 },
			{ type: "triangle", x: xStart + xSpan * 3, y: triYTop, size: size * 1.25, radius: size * 0.4 },

			// Circle
			{ type: "circle", x: xStart + xSpan * 4, y: circleY, size: size * 0.85 },

			// 4 squares: rounded → sharp
			{ type: "rect", x: xStart + xSpan * 5, y: sqYTop + step * 0.3, size: size * 0.85, radius: size * 0.4 },
			{ type: "rect", x: xStart + xSpan * 6, y: sqYMid2, size: size * 0.85, radius: size * 0.25 },
			{ type: "rect", x: xStart + xSpan * 7, y: sqYMid1, size: size * 0.85, radius: size * 0.12 },
			{ type: "rect", x: xStart + xSpan * 8, y: sqYBottom, size: size * 0.85, radius: 0 },
		];

		// Animation: start from circle position, stagger outward
		const circleX = xStart + xSpan * 4;

		this.shapes.forEach((shape, i) => {
			shape.startX = circleX;
			shape.startY = circleY;
			shape.endX = shape.x;
			shape.endY = shape.y;
			// Stagger from center outward
			const distFromCenter = Math.abs(i - 4);
			shape.delay = distFromCenter * 0.08;
		});
	}

	animate(timestamp) {
		if (!this.startTime) this.startTime = timestamp;

		const elapsed = timestamp - this.startTime;
		const progress = Math.min(elapsed / this.animationDuration, 1);

		this.draw(progress);

		if (progress < 1) {
			this.animationId = requestAnimationFrame(this.animateBound);
		}
	}

	draw(progress) {
		this.ctx.clearRect(0, 0, this.width, this.height);
		this.ctx.strokeStyle = this.strokeColor;
		this.ctx.lineWidth = this.strokeWidth;

		for (const shape of this.shapes) {
			const t = Math.max(
				0,
				Math.min(1, (progress - shape.delay) / (1 - shape.delay * 0.5))
			);
			if (t <= 0) continue;

			const eased = 1 - Math.pow(1 - t, 3);
			const x = shape.startX + (shape.endX - shape.startX) * eased;
			const y = shape.startY + (shape.endY - shape.startY) * eased;

			this.ctx.save();
			this.ctx.globalAlpha = eased;
			this.ctx.translate(x, y);

			if (shape.type === "triangle") {
				this.drawTriangle(shape.size, shape.radius);
			} else if (shape.type === "circle") {
				this.drawCircle(shape.size);
			} else if (shape.type === "rect") {
				this.drawRect(shape.size, shape.radius);
			}

			this.ctx.restore();
		}
	}

	drawTriangle(size, radius = 0) {
		const ctx = this.ctx;
		const r = size / 2;

		// Equilateral triangle vertices (apex at top)
		const p0 = { x: 0, y: -r };
		const p1 = { x: r * 0.866, y: r * 0.5 };
		const p2 = { x: -r * 0.866, y: r * 0.5 };

		ctx.beginPath();
		if (radius === 0) {
			ctx.moveTo(p0.x, p0.y);
			ctx.lineTo(p1.x, p1.y);
			ctx.lineTo(p2.x, p2.y);
			ctx.closePath();
		} else {
			const t = Math.min(radius / size, 0.3);
			const lerp = (a, b, amt) => ({
				x: a.x + (b.x - a.x) * amt,
				y: a.y + (b.y - a.y) * amt,
			});

			const a01 = lerp(p0, p1, t);
			const a10 = lerp(p1, p0, t);
			const a12 = lerp(p1, p2, t);
			const a21 = lerp(p2, p1, t);
			const a20 = lerp(p2, p0, t);
			const a02 = lerp(p0, p2, t);

			ctx.moveTo(a01.x, a01.y);
			ctx.lineTo(a10.x, a10.y);
			ctx.quadraticCurveTo(p1.x, p1.y, a12.x, a12.y);
			ctx.lineTo(a21.x, a21.y);
			ctx.quadraticCurveTo(p2.x, p2.y, a20.x, a20.y);
			ctx.lineTo(a02.x, a02.y);
			ctx.quadraticCurveTo(p0.x, p0.y, a01.x, a01.y);
		}
		ctx.stroke();
	}

	drawCircle(size) {
		this.ctx.beginPath();
		this.ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
		this.ctx.stroke();
	}

	drawRect(size, radius) {
		const h = size / 2;
		const r = Math.min(radius, h);

		this.ctx.beginPath();
		this.ctx.moveTo(-h + r, -h);
		this.ctx.lineTo(h - r, -h);
		this.ctx.arcTo(h, -h, h, -h + r, r);
		this.ctx.lineTo(h, h - r);
		this.ctx.arcTo(h, h, h - r, h, r);
		this.ctx.lineTo(-h + r, h);
		this.ctx.arcTo(-h, h, -h, h - r, r);
		this.ctx.lineTo(-h, -h + r);
		this.ctx.arcTo(-h, -h, -h + r, -h, r);
		this.ctx.closePath();
		this.ctx.stroke();
	}

	handleResize() {
		this.setupCanvas();
		this.createShapes();
		this.draw(1);
	}

	destroy() {
		window.removeEventListener("resize", this.handleResizeBound);
		if (this.animationId) {
			cancelAnimationFrame(this.animationId);
		}
	}
}
